import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Users, Target, HeartHandshake, Sparkles, MapPin, Briefcase, Award, ArrowRight } from "lucide-react";

const VALUES = [
  {
    icon: HeartHandshake,
    title: "Côte à côte, pas en face",
    body: "On ne livre pas un rapport et on disparaît. On vous accompagne, on prend la décision avec vous, on apprend de vos contraintes terrain.",
  },
  {
    icon: Target,
    title: "Résultats concrets",
    body: "Pas de slides en PowerPoint. Des actions livrées, des KPIs mesurables, un ROI traçable mois après mois.",
  },
  {
    icon: Sparkles,
    title: "Sur-mesure",
    body: "Chaque entreprise a son ADN. On adapte le plan, les outils, le rythme — jamais un copier-coller depuis un client précédent.",
  },
];

const MILESTONES = [
  { year: "2005", label: "Premiers accompagnements", desc: "Conseil indépendant aux PME locales du Sud-Est." },
  { year: "2012", label: "Spécialisation digitale", desc: "Bascule vers le développement commercial digital et les outils sur-mesure." },
  { year: "2018", label: "Pack identité & MVP", desc: "Création de l'offre Identité + MVP pour les porteurs de projets." },
  { year: "2026", label: "Plateforme BESSON SOLUTIONS", desc: "Lancement du back-office complet : Espace Entreprise, IA, suivi opportunités." },
];

export default function About() {
  return (
    <>
      <Header />
      <main data-testid="about-page" className="bg-white">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white pt-40 sm:pt-44 pb-20 sm:pb-24 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-25 pointer-events-none" style={{ background: "#06B6D4", filter: "blur(140px)" }} />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full opacity-15 pointer-events-none" style={{ background: "#FFDCA8", filter: "blur(140px)" }} />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-[11px] font-extrabold tracking-[0.32em] uppercase text-[#67E8F9]">Qui sommes-nous ?</div>
            <h1 className="display mt-4 text-4xl sm:text-6xl font-extrabold leading-[1.05]">
              Nous sommes <em className="not-italic text-[#67E8F9]">les copilotes</em><br />
              de votre activité.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-white/80 max-w-2xl leading-relaxed">
              BESSON SOLUTIONS, c'est <b>20 ans de terrain</b> au service des dirigeants, entrepreneurs et porteurs de projet du Sud-Est de la France et au-delà. Pas un cabinet derrière un bureau, mais une équipe qui s'engage avec vous.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-white/75 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <MapPin size={14} className="text-[#67E8F9]" />
              <span>Basés à <b className="text-white">Istres</b>, en plein cœur du Sud-Est · interventions partout en France</span>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20 sm:py-28 max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[11px] font-extrabold tracking-[0.32em] uppercase text-[#06B6D4]">Notre mission</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl text-[#0A1128] font-extrabold leading-[1.1]">
                Faire grandir <em className="not-italic relative">
                  <span className="relative z-10">les bonnes idées</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-[#A7F3D0] -z-0 rounded-sm" />
                </em>, sans jargon.
              </h2>
              <p className="mt-5 text-[#475569] leading-relaxed">
                On croit qu'un entrepreneur n'a pas besoin d'un rapport de 80 pages : il a besoin d'un plan clair, d'un partenaire qui décroche le téléphone, et d'outils qui marchent dès le lundi matin.
              </p>
              <p className="mt-3 text-[#475569] leading-relaxed">
                Que vous vouliez <b className="text-[#0A1128]">structurer une activité</b> qui existe ou <b className="text-[#0A1128]">faire émerger</b> un projet qui n'a pas encore décollé — on traite ça comme si c'était notre propre entreprise.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&w=1280&q=70"
                alt="L'équipe BESSON SOLUTIONS"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#FFDCA8] rounded-2xl px-4 py-3 shadow-xl">
                <div className="text-[10px] tracking-widest uppercase text-[#92400E] font-bold">Sud-Est de la France</div>
                <div className="display font-extrabold text-[#0A1128] text-lg">Istres · Marseille · Aix</div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-[#FAFAFA] py-20 sm:py-28">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <div className="text-[11px] font-extrabold tracking-[0.32em] uppercase text-[#06B6D4]">Nos valeurs</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl text-[#0A1128] font-extrabold leading-[1.1]">
                Ce qui nous distingue.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {VALUES.map((v) => (
                <div key={v.title} className="bg-white rounded-3xl p-6 border border-black/5 hover:shadow-xl transition-shadow">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#06B6D4]/10 text-[#06B6D4] mb-4">
                    <v.icon size={22} />
                  </div>
                  <h3 className="display font-extrabold text-[#0A1128] text-lg">{v.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed mt-2">{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 sm:py-28 max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="text-[11px] font-extrabold tracking-[0.32em] uppercase text-[#06B6D4]">Notre histoire</div>
            <h2 className="display mt-3 text-3xl sm:text-4xl text-[#0A1128] font-extrabold leading-[1.1]">
              20 ans, 14 entreprises accompagnées, 0 dossier abandonné.
            </h2>
          </div>
          <div className="relative pl-8 sm:pl-12 border-l-2 border-[#06B6D4]/30">
            {MILESTONES.map((m, i) => (
              <div key={m.year} className="relative pb-10 last:pb-0">
                <span className="absolute -left-[37px] sm:-left-[53px] top-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#06B6D4] text-[#0A1128] font-bold text-xs shadow-lg">
                  <Briefcase size={12} />
                </span>
                <div className="display font-extrabold text-[#06B6D4] text-sm tracking-wider">{m.year}</div>
                <div className="display font-bold text-[#0A1128] text-lg mt-0.5">{m.label}</div>
                <p className="text-[#475569] text-sm mt-1">{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white py-20 sm:py-24 text-center relative overflow-hidden">
          <div className="absolute inset-0 noise opacity-25" />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
            <Award size={36} className="mx-auto text-[#FFDCA8]" />
            <h2 className="display mt-4 text-3xl sm:text-5xl font-extrabold leading-[1.05]">
              On échange ?
            </h2>
            <p className="mt-4 text-white/80 text-lg">
              1er RDV découverte offert. 15 min au téléphone ou 30 min en physique à Istres. Sans engagement.
            </p>
            <Link
              to="/rendez-vous"
              data-testid="about-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-extrabold text-[15px] shadow-2xl transition-all hover:scale-[1.02]"
            >
              Prendre RDV en ligne
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
